<template>
  <PopoverRoot v-model="open" :no-overlay="true">
    <header
      :class="[
        open ? 'fixed inset-0 z-40 overflow-y-auto' : '',
        'bg-white shadow-sm lg:static lg:overflow-y-visible z-10',
      ]"
    >
      <div class="px-4 md:px-10 py-[5.5px]">
        <div
          class="relative flex justify-between space-x-5 lg:space-x-8 xl:space-x-12 items-center"
        >
          <div class="flex md:inset-y-0 md:left-0 lg:static xl:col-span-2">
            <div class="flex flex-shrink-0 items-center">
              <NuxtLink :to="localePath('/')">
                <NuxtImg
                  v-if="logo"
                  class="h-[75px] w-[75px] sm:h-[80px] sm:w-[80px]"
                  src="/logo.png"
                  :alt="logo?.image?.alt"
                />
              </NuxtLink>
            </div>
          </div>
          <div>
            <DjHeaderBottom />
          </div>
          <div class="min-w-0 flex-1 md:px-8 lg:px-0 xl:col-span-5">
            <div
              class="flex items-center px-3 py-2 md:mx-auto md:max-w-3xl lg:mx-0 lg:max-w-none xl:px-0"
            >
              <div class="w-full">
                <DjSearchBar class="hidden md:block" button-type="primary" />
              </div>
            </div>
          </div>
          <ClientOnly>
            <div class="flex items-center justify-end xl:col-span-5">
              <DjIconWithText
                :is-disabled="!isAuthenticated"
                :use-u-icon="true"
                :icon-class="isAuthenticated ? 'header-icon' : 'header-icon disabled'"
                :notification="isAuthenticated ? nbQuotes! : undefined"
                icon="heroicons:document-text"
                :text="$t('DjHeaderMain.quotes')"
                link="/account/quotes"
                class="flex-none hidden lg:block"
              />

              <DjIconWithText
                :is-disabled="!isAuthenticated"
                :use-u-icon="true"
                :icon-class="isAuthenticated ? 'header-icon' : 'header-icon disabled'"
                icon="heroicons:shopping-cart"
                :notification="isAuthenticated ? nbProducts! : undefined"
                :text="$t('DjHeaderMain.cart')"
                @click="showCart"
              />

              <DjIconWithText
                :is-disabled="!isAuthenticated"
                :use-u-icon="true"
                :icon-class="isAuthenticated ? 'header-icon' : 'header-icon disabled'"
                icon="heroicons:heart"
                :notification="nbBuyingList"
                :text="$t('DjHeaderMain.buyinglists')"
                :link="localePath('/account/buyinglists')"
                class="hidden lg:block"
              />

              <div>
                <UDropdownMenu
                  :items="userNavigation"
                  class="hidden lg:block"
                  :content="{
                    align: 'start',
                    side: 'bottom',
                    sideOffset: 8,
                  }"
                  :ui="{ content: 'w-48', background: 'bg-white' }"
                >
                  <UButton
                    v-if="isAuthenticated"
                    color="neutral"
                    variant="ghost"
                    class="hover:bg-transparent"
                    @click.stop
                  >
                    <DjIconWithText
                      v-if="userName"
                      :use-u-icon="true"
                      :is-disabled="false"
                      icon="heroicons:user"
                      :text="userName"
                      text-class="max-w-[120px] truncate"
                    />
                    <div v-else class="flex items-center space-x-2">
                      <USkeleton class="h-6 w-6 rounded-full" />
                      <div>
                        <USkeleton class="h-4 w-24 mb-1" />
                        <USkeleton class="h-4 w-32" />
                      </div>
                    </div>
                  </UButton>
                </UDropdownMenu>
              </div>
              <div class="hidden lg:block">
                <UButton
                  variant="ghost"
                  color="neutral"
                  class="hover:bg-gray-100 p-2"
                  @click="toggleHeaderTop"
                >
                  <UIcon name="heroicons:cog-6-tooth" class="w-5 h-5 text-gray-600 hover:text-gray-900" />
                </UButton>
              </div>
              <div class="flex items-center lg:hidden m-4">
                <PopoverTrigger as-child>
                  <UButton
                    variant="ghost"
                    class="relative -mx-2 inline-flex items-center justify-center rounded-md p-2"
                  >
                    <span class="absolute -inset-0.5" />
                    <UIcon
                      v-if="!open"
                      name="i-heroicons-outline-bars-3"
                      class="block h-6 w-6"
                      aria-hidden="true"
                    />
                    <UIcon
                      v-else
                      name="i-heroicons-outline-x-mark"
                      class="block h-6 w-6"
                      aria-hidden="true"
                    />
                  </UButton>
                </PopoverTrigger>
                <UButton
                  variant="ghost"
                  class="relative -mx-2 inline-flex items-center justify-center rounded-md p-2 ml-2"
                  @click="toggleHeaderTop"
                >
                  <UIcon name="heroicons:cog-6-tooth" class="w-5 h-5 text-gray-600" />
                </UButton>
              </div>
            </div>
          </ClientOnly>
        </div>
        <DjSearchBar class="block md:hidden py-3 md:py-0" />
      </div>
    </header>

    <PopoverPortal>
      <PopoverContent
        v-if="!hideMenu"
        as="nav"
        class="lg:hidden w-screen bg-white shadow-md"
        aria-label="Global"
        :align-offset="16"
      >
        <div v-if="isAuthenticated" class="border-t border-gray-200 pb-3 pt-4 cursor-pointer">
          <div class="mx-auto flex max-w-3xl items-center px-4 sm:px-6">
            <div class="ml-3">
              <div class="text-base font-medium text-gray-800 truncate max-w-[280px]">
                {{ userName }}
              </div>
              <div class="text-sm font-medium text-gray-500 truncate max-w-[280px]">
                {{ userEmail }}
              </div>
            </div>
            <button
              type="button"
              class="relative ml-auto flex-shrink-0 rounded-full bg-white p-1 text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
            >
              <span class="absolute -inset-1.5" />
              <UIcon name="i-heroicons-outline-bell" class="h-6 w-6" aria-hidden="true" />
            </button>
          </div>
          <div class="mx-auto mt-3 max-w-3xl space-y-1 px-2 sm:px-4 flex flex-col">
            <a
              v-for="item in userNavigation"
              :key="item.label"
              :href="item.href"
              :class="`${item.class}`"
              class="rounded-md px-3 py-2 text-base font-medium text-gray-500 hover:bg-gray-50 hover:text-gray-900"
            >
              {{ item.label }}
            </a>
          </div>
        </div>

        <div class="border-t border-gray-200 mx-auto mt-3 max-w-3xl space-y-1 px-4 sm:px-4">
          <div class="-my-6 divide-y divide-gray-500/10">
            <div class="space-y-2 py-6">
              <UCollapsible v-model:open="openCategories" class="-mx-2.5">
                <div
                  class="flex w-full items-center justify-between rounded-md px-3 py-2 mt-6 text-base font-medium text-gray-500 hover:bg-gray-50 hover:text-gray-900"
                >
                  {{ $t('DjHeaderMain.products') }}
                  <Icon
                    name="heroicons:chevron-down-20-solid"
                    :class="[openCategories ? 'rotate-180' : '', 'h-5 w-5 flex-none']"
                    aria-hidden="true"
                  />
                </div>
                <template #content>
                  <div class="mt-2 overflow-auto space-y-2 max-h-[280px]">
                    <!-- Render the CategoryDisclosure for each top-level category -->
                    <DjCategoryCollapsible
                      v-for="item in NavCategories"
                      :key="item.id"
                      :category="item"
                      :class="`${item.class}`"
                    />
                  </div>
                </template>
              </UCollapsible>
              <a
                href="/fastcart"
                class="flex w-full items-center justify-between rounded-md px-0 py-2 text-base font-medium text-gray-500 hover:bg-gray-50 hover:text-gray-900"
              >
                {{ $t('DjHeaderMain.fastCart') }}
              </a>
            </div>
            <div v-if="isAuthenticated" class="py-6">
              <a
                href="#"
                class="flex w-full items-center justify-between rounded-md px-0 py-2 text-base font-medium text-gray-500 hover:bg-gray-50 hover:text-gray-900"
                @click.prevent="logOutFunction"
              >
                {{ $t('DjHeaderMain.logout') }}
              </a>
            </div>
          </div>
        </div>
      </PopoverContent>
    </PopoverPortal>
  </PopoverRoot>
</template>

<script setup lang="ts">
  import { PopoverRoot, PopoverTrigger, PopoverContent, PopoverPortal } from 'reka-ui';

  defineOptions({
    name: 'DjHeaderMain',
  });

  const { t } = useI18n();

  const { logout } = useDjustAuth();
  const useStore = useUserStore();

  const user = computed(() => useStore.user);
  const isAuthenticated = computed(() => useStore.isAuthenticated);
  const route = useRoute();
  const router = useRouter();
  const localePath = useLocalePath();
  const { getAuthenticatedUser } = useDjustAuth();
  const { getUserEmail, getUserFullName } = useUser();

  await getAuthenticatedUser();

  const userName = computed(() => {
    if (user.value) return getUserFullName(user.value);
    return '';
  });

  const userEmail = computed(() => {
    if (user.value) getUserEmail(user.value);
    return '';
  });

  const open = ref(false);
  const openCategories = ref(false);
  const { getNavigationCategories } = useNavCategory();
  const { locale } = useI18n();

  const { categories: NavCategories } = await getNavigationCategories(locale.value);
  const headerTopControls = inject('headerTopControls', {
    showHeaderTop: () => {},
    hideHeaderTop: () => {},
    toggleHeaderTop: () => {}
  });

  const { toggleHeaderTop } = headerTopControls;

  const userNavigation = [
    {
      label: t('DjProfilePage.breadcrumb.profile'),
      href: localePath('/account/user-information'),
      class: '',
    },
    {
      label: t('DjCustomerAccountPage.account'),
      href: localePath('/account/customer-account'),
      class: '',
    },
    { label: t('DjAddressesPage.title'), href: localePath('/account/addresses'), class: '' },
    { label: t('DjOrdersPage.title'), href: localePath('/account/orders'), class: '' },
    {
      label: t('DjHeaderMain.buyinglists'),
      href: localePath('/account/buyinglists'),
      class: 'lg:hidden',
    },
    { label: t('DjHeaderMain.quotes'), href: localePath('/account/quotes'), class: 'lg:hidden' },
    { label: t('DjHeaderMain.contact'), href: localePath('/contact'), class: '' },
    {
      label: t('DjHeaderMain.logout'),
      onClick() {
        logOutFunction();
      },
      class: 'hidden lg:flex',
    },
  ];

  const storyblokStore = useStoryblokStore();
  import DjCartLayer from '../../templates/DjCartLayer/DjCartLayer.vue';

  const overlay = useOverlay();
  const cartLayer = overlay.create(DjCartLayer);

  function showCart() {
    cartLayer.open();
  }
  function hideCart() {
    cartLayer.close();
  }

  function showClass(index: number) {
    return index === userNavigation.length - 1 ? 'hidden lg:block' : 'block ';
  }

  const redirectedPath = ['/checkout', '/auth', '/product'];
  const routePath = computed(() => route.path);
  watch(routePath, (newValue: any) => {
    if (redirectedPath.some((path: string) => newValue.startsWith(path))) {
      hideCart();
    }
  });
  storyblokStore.fetchStory('logo');

  const logo = computed(() => storyblokStore.getStoryByName('logo'));

  const { getNumberOfProducts } = useCart();
  const cartStore = useCartStore();
  const quoteStore = useQuoteStore();
  const buyingListStore = useBuyingListStore();
  const nbProducts = computed(() =>
    isAuthenticated.value && cartStore?.cart ? getNumberOfProducts(cartStore?.cart) : null
  );
  const nbQuotes = computed(() =>
    isAuthenticated.value && quoteStore?.quotes ? quoteStore?.quotes.length : null
  );
  const nbBuyingList = computed(() =>
    isAuthenticated.value && buyingListStore?.buyinglist.length
      ? buyingListStore.buyinglist.length
      : 0
  );

  async function logOutFunction(): Promise<void> {
    await logout().then(() => {
      open.value = false;
      router.push('/auth/login');
    });
  }
</script>

<style lang="scss" scoped>
  .dj-block--user-account {
    position: relative;
    i {
      font-size: 30px;
      line-height: 1em;
    }
    .dj-list--arrow {
      li {
        cursor: pointer;
        padding: 10px;
        a {
          padding-left: 0;
          &:before {
            display: none;
          }
        }
      }
    }

    .dj-block__content {
      background-color: #fff;
      position: absolute;
      min-width: 240px;
      right: 0;
      z-index: 30;
      padding-top: 10px;
      transform: translate(0 0);
      transition: all 0.4s ease;
      visibility: hidden;
      &.order-types {
        min-width: 140px;
      }
    }
    &:hover {
      i {
        cursor: pointer;
      }
      .dj-block__content {
        visibility: visible;
        transform: translate(0 20px) scale3d(1, 1, 1);
        padding: 10px 15px;
      }
    }
  }
</style>
